import { useMemo, useState } from 'react';
import { useBoardV2 } from '../../hooks/useBoardV2';
import { hasActivity, type DealV2 } from '../../types';
import { ACTIVITY_UNAVAILABLE_REASON } from '../../utils/activityCopy';
import { formatMoneyMm } from '../../utils/formatMoneyMm';
import type { SortDirection, SortKey } from '../../utils/sortDealsV2';
import { SummaryCards } from '../layout/SummaryCards';
import { DealTableV2 } from '../sections/DealTableV2';
import type { ActivityMode } from '../shared/ActivityIndicator';
import { DropdownToggle } from '../shared/DropdownToggle';
import { MultiSelectFilter } from '../shared/MultiSelectFilter';
import { PersonFilter } from '../shared/PersonFilter';
import { SectionNumber } from '../shared/SectionNumber';
import styles from './PipelineV2Page.module.css';
import sectionStyles from '../sections/Section.module.css';
import pipelineControlsStyles from '../layout/PipelineControls.module.css';

/**
 * DASH-2v — the pipeline board sourced from OUR data instead of the Affinity snapshot.
 *
 * Runs BESIDE the Pipeline tab during parallel-run so v1 and v2 can be compared on the same deals while
 * Affinity is still live. That comparison is the point: v1 goes stale by construction the moment
 * Affinity stops being updated, so v2 has to be trusted BEFORE the cut, not after. At the cut this
 * replaces the Pipeline tab and v1's copy is deleted.
 *
 * **Firm-visible since 2026-08-10, with no carve-out since 2026-08-13**: the ACTIVITY numbers used to
 * stay admin-only pending the DASH-2a parity artifact, and that hold was retired once it was clear the
 * signature could never be produced (the Affinity comparator froze at the cut). Every viewer of the
 * board now gets the same payload, so the disabled-with-reason copy names one cause again — the read
 * flag; see `utils/activityCopy.ts`. Expect the numbers to be PARTIAL rather than absent: attribution
 * runs through recorded deal↔person links, and 55 of 160 real deals carried native activity at the
 * 2026-08-08 measure, so a flat sparkline on the rest is coverage rather than a rendering bug.
 *
 * **The control surface mirrors v1's deliberately, control for control**, because the two boards are
 * compared side by side and a *missing* filter is indistinguishable from a filter that silently
 * disagrees. Where v2 genuinely cannot answer, the control is RENDERED AND DISABLED with the reason
 * on hover, rather than omitted. An absent control invites "v2 can't group"; a disabled one says why.
 *
 * **The activity metric and the 7d-active partition are live as of DASH-2a 2a-4 tier 1** — but only
 * when the payload actually carries counts (backend: the read flag on AND an admin caller). Both fall
 * back to the disabled-with-reason state otherwise, because a toggle that silently partitions on
 * absent data would put every deal in "Inactive" and read as a v1↔v2 discrepancy.
 *
 * **The weekly sparkline is live as of DASH-2b W3** (`docs/specs/strategic-architecture/dash-2b-weekly-series-spec.md`
 * §5). The page owns the bar scale — one mode-aware `activityGlobalMax` spanning every section — so
 * bars are comparable across the whole board rather than within a section or a row.
 *
 * **W0 board polish (owner-directed).** The pre-parity explainer banner and the `residue` warning
 * panel are gone: both were review scaffolding for the parallel run, and the board is read as a board
 * now. Where a control still cannot answer, the reason lives on the control itself (hover title) and
 * in the table's column caption, which is where a reader is already looking. `residue` is still in the
 * payload and the types — it is no longer drawn on this page.
 */

/**
 * The board's sections, each drawn from one or more payload section keys.
 *
 * **`screening` has no band of its own (owner-directed, 2026-08-13) — it is folded into Origination.**
 * That is not a new opinion, it is the one the rest of the system already held: v1's page renders
 * `[...origination, ...screening]` under a single Origination heading, and `board_v2._summary_cards`
 * counts the same union behind the "Origination" tile. Only this page still drew a third band, and no
 * stage maps to `screening` in the seeded `stage_mapping`, so it rendered as a permanent "03
 * Screening · 0 deals" header.
 *
 * **Folded, not dropped — deliberate, not tidiness.** The mapping is DATA (`stage_mapping.csv` seeds
 * it; the DB is authority), and this change was authored without a prod read, so a page that simply
 * stopped rendering the key would silently drop any deal mapped there. Folding makes the seed-vs-prod
 * question moot: a stage mapped to `screening` shows up under Origination rather than nowhere.
 */
const SECTIONS: {
  keys: string[];
  label: string;
  number: string;
  /** v1 tints unassigned rows in its origination-side sections only, not on Live. */
  tintUnassigned: boolean;
}[] = [
  { keys: ['live'], label: 'Live Deals', number: '02', tintUnassigned: false },
  { keys: ['origination', 'screening'], label: 'Origination', number: '03', tintUnassigned: true },
  { keys: ['add_ons'], label: 'Add-Ons', number: '04', tintUnassigned: false },
  // WP2/S2.1 (portco-transactions spec §3, ruling R2): exits get their OWN band. The payload
  // is generically keyed (`sections: dict[str, list[...]]`), so this registry entry is the
  // whole front-end change — no schema, no new fetch.
  // `tintUnassigned: false` deliberately: the tint means 'nobody has picked this up yet',
  // which is an ORIGINATION question. A live sale process with no deal team is a staffing
  // fact, not an unclaimed lead, and tinting it would read as the latter.
  { keys: ['exits'], label: 'Exits', number: '05', tintUnassigned: false },
];

/** Every payload key the board renders — the flat list, for the page-wide scans below. */
const SECTION_KEYS = SECTIONS.flatMap((s) => s.keys);

function totalEv(deals: DealV2[]): number {
  return deals.reduce((sum, d) => sum + (d.enterprise_value ?? 0), 0);
}

function firstNameLabel(name: string): string {
  return name.split(' ')[0];
}

/** The toggle chrome v1 uses for its group-by buttons, as a component so the five agree. */
function ToggleButton({
  on,
  label,
  onClick,
  tone = 'blue',
  disabled = false,
  title,
}: {
  on: boolean;
  label: string;
  onClick?: () => void;
  tone?: 'blue' | 'green';
  disabled?: boolean;
  title?: string;
}) {
  const accent = tone === 'green' ? '#2E7D32' : '#1565C0';
  const tint = tone === 'green' ? '#E8F5E9' : '#E3F2FD';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={styles.toggle}
      style={{
        border: `1px solid ${on ? accent : '#E5E5E0'}`,
        background: on ? tint : '#fff',
        color: disabled ? '#C4C4BE' : on ? accent : '#999',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function BoardSection({
  label,
  number,
  deals,
  groupByLead,
  groupByType,
  highlightUpdates,
  tintUnassigned,
  listId,
  activityMode,
  activityGlobalMax,
  groupByActive,
}: {
  label: string;
  number: string;
  deals: DealV2[];
  groupByLead: boolean;
  groupByType: boolean;
  highlightUpdates: boolean;
  tintUnassigned: boolean;
  activityMode: ActivityMode;
  /** Board-wide bar scale for the weekly sparkline — computed once on the page, never per section. */
  activityGlobalMax: number;
  groupByActive: boolean;
  /** The board's CRM list — the "Pending Update" badge's panel link needs it (badge spec §1.1). */
  listId?: number | null;
}) {
  const [stageFilter, setStageFilter] = useState<Set<string>>(new Set());
  const [teamFilter, setTeamFilter] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>('stage');
  const [sortDir, setSortDir] = useState<SortDirection>('asc');
  const [collapsed, setCollapsed] = useState(false);

  const onSortChange = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Stage options in the mapping's own order, narrowed to the stages actually present — v1's rule, so
  // the dropdown lists the same stages in the same order on both boards.
  // WARNING - THIS USED TO READ `getStageOrder()`, AND THAT WAS BROKEN ON THIS PAGE.
  // `setStageOrder` is populated only by `usePipeline.useStages`, which the v1 surfaces fetch
  // and this page never did — so on a session that opened the native board first, the filter
  // ordered itself by the HARDCODED seven-stage fallback in `utils/stageOrder.ts` and sorted
  // every other stage to the end in payload order. All nine exit stages would have landed
  // there (build plan S2.1: 'wire it, or the exit stages appear in an arbitrary order').
  //
  // Fixed by deriving the order from the PAYLOAD rather than by adding a second fetch: every
  // deal already carries `display_order`, straight from `stage_mapping`. That is strictly
  // better than wiring the global — the board's filter now orders by the DB's own numbers,
  // cannot go stale against a module singleton, and needs no fallback list to maintain.
  //
  // Ascending `display_order` reproduces today's order exactly (the deal ladder is stored
  // inverted: `In Exclusivity/DD` is 1), and puts the exit ladder's 200-block after it. Ties
  // break on the name so the list is deterministic when a stage is unmapped (order 999).
  const stageOptions = useMemo(() => {
    const orderOf = new Map<string, number>();
    for (const d of deals) {
      const existing = orderOf.get(d.stage);
      if (existing === undefined || d.display_order < existing) orderOf.set(d.stage, d.display_order);
    }
    return Array.from(orderOf.entries())
      .sort((a, b) => (a[1] - b[1]) || a[0].localeCompare(b[0]))
      .map(([stage]) => ({ key: stage, label: stage }));
  }, [deals]);

  const teamOptions = useMemo(() => {
    const names = new Set<string>();
    for (const deal of deals) for (const m of deal.deal_team) names.add(m.name);
    return Array.from(names)
      .sort((a, b) => firstNameLabel(a).localeCompare(firstNameLabel(b)))
      .map((name) => ({ key: name, label: firstNameLabel(name) }));
  }, [deals]);

  const filtered = useMemo(
    () =>
      deals.filter((d) => {
        if (stageFilter.size > 0 && !stageFilter.has(d.stage)) return false;
        if (teamFilter.size > 0) {
          const names = d.deal_team.map((m) => m.name);
          if (!names.some((n) => teamFilter.has(n))) return false;
        }
        return true;
      }),
    [deals, stageFilter, teamFilter],
  );

  // Counted over the UNFILTERED section, matching v1: the header describes the section, not the
  // current filter, so toggling a filter does not appear to change how big the pipeline is.
  const ev = useMemo(() => totalEv(deals), [deals]);
  const unassigned = useMemo(() => deals.filter((d) => d.deal_team.length === 0).length, [deals]);

  return (
    // v1's exact section chrome — .section > .card > .headerBar[data-section-header] > SectionNumber.
    // Rendering SectionNumber without the dark header bar is what made the v2 headers look washed out.
    // `data-section-header` is load-bearing, not decoration: useStickyHeaderOffsets measures it to
    // compute the table's sticky offset, so without it the column header pins at the wrong height.
    <div className={sectionStyles.section}>
      <div className={sectionStyles.card}>
        <div className={sectionStyles.headerBar} data-section-header>
          <SectionNumber
            number={number}
            label={label}
            count={deals.length}
            countLabel={deals.length === 1 ? 'deal' : 'deals'}
            sublabel={
              ev > 0
                ? `${formatMoneyMm(Math.round(ev))} EV · ${unassigned} unassigned`
                : `${unassigned} unassigned`
            }
            collapsed={collapsed}
            onToggle={() => setCollapsed(!collapsed)}
          />
          <div className={sectionStyles.headerBarFilters}>
            <MultiSelectFilter
              label="Stage"
              options={stageOptions}
              selected={stageFilter}
              onChange={setStageFilter}
            />
            <MultiSelectFilter
              label="Team"
              options={teamOptions}
              selected={teamFilter}
              onChange={setTeamFilter}
            />
          </div>
        </div>
        {!collapsed &&
          (filtered.length === 0 ? (
            <div className={sectionStyles.empty}>
              {deals.length === 0 ? 'No deals in this section.' : 'No deals match these filters.'}
            </div>
          ) : (
            // NOT showSource: v1's Origination section renders STATUS (the stage) like every other
            // section — passing showSource replaced the stage AND suppressed days-in-stage.
            //
            // Group-by-lead is expressed as a SORT KEY, exactly as v1 does it: the table draws a
            // group band whenever `sortKey === 'lead'` and the lead changes, so routing the toggle
            // through sortKey is what keeps the two boards' banding identical.
            <DealTableV2
              deals={filtered}
              sortKey={groupByLead ? 'lead' : sortKey}
              sortDirection={groupByLead ? 'asc' : sortDir}
              onSortChange={onSortChange}
              groupByType={groupByType}
              highlightUpdates={highlightUpdates}
              isUnassignedRow={tintUnassigned ? (d) => d.deal_team.length === 0 : undefined}
              listId={listId}
              activityMode={activityMode}
              activityGlobalMax={activityGlobalMax}
              groupByActive={groupByActive}
            />
          ))}
      </div>
    </div>
  );
}

export function PipelineV2Page() {
  const board = useBoardV2();

  const [personFilter, setPersonFilter] = useState<Set<string>>(new Set());
  const [personFilterMode, setPersonFilterMode] = useState<'any' | 'all'>('any');
  const [highlightUpdates, setHighlightUpdates] = useState(false);
  // Grouping by deal lead is the board's DEFAULT (owner-directed, 2026-08-09). Still a toggle —
  // switching it off returns each section to its own sort.
  const [groupByLead, setGroupByLead] = useState(true);
  const [groupByType, setGroupByType] = useState(false);
  const [activityMode, setActivityMode] = useState<ActivityMode>('total');
  const [groupByActive, setGroupByActive] = useState(false);

  const data = board.data;

  // Does the payload carry computed activity? One question for the whole page, asked of any board row
  // — the backend populates the fields for every row or for none (one flag, one read), so a single
  // deal answers it. `hasActivity` tests for null explicitly: a genuinely quiet deal has `0`, and
  // reading that as "no data" would disable the controls exactly when the numbers are correct.
  const activityPresent = useMemo(
    () => SECTION_KEYS.some((key) => (data?.sections[key] ?? []).some(hasActivity)),
    [data],
  );

  // The sparkline's bar scale, mode-aware and BOARD-WIDE — v1's `App.tsx` memo, same semantics
  // (spec §5). It has to be computed here rather than inside `ActivityIndicator` or `DealTableV2`,
  // because a scale derived from one deal's or one section's weeks makes every deal's tallest week
  // look the same height: a deal with two emails would draw like the busiest deal on the board. The
  // floor of 1 keeps the division safe on an all-zero board; the mode dependency is what makes
  // switching to Meetings rescale instead of flattening a meetings-light board against email volume.
  const activityGlobalMax = useMemo(() => {
    let max = 0;
    for (const key of SECTION_KEYS) {
      for (const deal of data?.sections[key] ?? []) {
        for (const w of deal.activity_weeks ?? []) {
          const v =
            activityMode === 'total'
              ? w.emails + w.meetings
              : activityMode === 'meetings'
                ? w.meetings
                : w.emails;
          if (v > max) max = v;
        }
      }
    }
    return Math.max(1, max);
  }, [data, activityMode]);

  // Person options span EVERY board section, not the one being rendered — a global filter whose
  // option list depended on a section would drop people the filter can still match.
  const personOptions = useMemo(() => {
    if (!data) return [];
    const names = new Set<string>();
    for (const key of SECTION_KEYS) {
      for (const deal of data.sections[key] ?? []) {
        for (const m of deal.deal_team) names.add(m.name);
      }
    }
    return Array.from(names)
      .sort((a, b) => firstNameLabel(a).localeCompare(firstNameLabel(b)))
      .map((name) => ({ key: name, label: firstNameLabel(name) }));
  }, [data]);

  if (board.isLoading) return <div className={styles.notice}>Loading the native board…</div>;
  if (board.error) {
    return (
      <div className={styles.notice}>
        Could not load the native board: {String((board.error as Error).message || board.error)}
      </div>
    );
  }
  if (!data) return <div className={styles.notice}>No board data.</div>;

  const filterByPerson = (deals: DealV2[]): DealV2[] => {
    if (personFilter.size === 0) return deals;
    return deals.filter((d) => {
      const names = d.deal_team.map((m) => m.name);
      // v1's semantics exactly: 'any' = intersects the selection, 'all' = contains all of it.
      if (personFilterMode === 'any') return names.some((n) => personFilter.has(n));
      return [...personFilter].every((n) => names.includes(n));
    });
  };

  return (
    <div className={styles.root}>
      <SummaryCards cards={data.summary} />

      <div className={pipelineControlsStyles.bar}>
        <span className={pipelineControlsStyles.label}>Pipeline v2</span>
        <div className={pipelineControlsStyles.controls}>
          <PersonFilter
            options={personOptions}
            selected={personFilter}
            onChange={setPersonFilter}
            mode={personFilterMode}
            onModeChange={setPersonFilterMode}
          />
          {/* v1's own control, same three options and same semantics — a second implementation of
              "which activity figure" is how the two boards start showing different numbers. */}
          {activityPresent ? (
            <DropdownToggle<ActivityMode>
              variant="light"
              ariaLabel="Activity metric"
              value={activityMode}
              onChange={setActivityMode}
              options={[
                { value: 'total', label: 'Total' },
                { value: 'meetings', label: 'Meetings' },
                { value: 'emails', label: 'Emails' },
              ]}
            />
          ) : (
            <ToggleButton
              on={false}
              disabled
              label="Activity: Total"
              title={ACTIVITY_UNAVAILABLE_REASON}
            />
          )}
          <ToggleButton
            on={highlightUpdates}
            tone="green"
            label={highlightUpdates ? 'Updates Highlighted' : 'Highlight Updates'}
            onClick={() => setHighlightUpdates((h) => !h)}
            title={
              activityPresent
                ? 'Tint deals that are new, have moved stage, or had a meeting in the last 7 days — v1’s three predicates.'
                : `Tint deals that are new or have moved stage. v1 also highlights deals with a meeting in the last 7 days; that branch needs the activity numbers. ${ACTIVITY_UNAVAILABLE_REASON}`
            }
          />
          <ToggleButton
            on={groupByActive && activityPresent}
            disabled={!activityPresent}
            label="Group by 7d Active"
            onClick={activityPresent ? () => setGroupByActive((g) => !g) : undefined}
            title={
              activityPresent
                ? 'Partition into Active / Inactive on v1’s predicate: any email or meeting in the last 7 days, or a new/moved badge.'
                : `Active/inactive is derived from 7-day email and meeting counts. ${ACTIVITY_UNAVAILABLE_REASON}`
            }
          />
          <ToggleButton
            on={groupByLead}
            label="Group by Deal Lead"
            onClick={() => {
              setGroupByLead((g) => !g);
              setGroupByType(false);
            }}
          />
          <ToggleButton
            on={groupByType}
            label="Group by Type"
            onClick={() => {
              setGroupByType((t) => !t);
              setGroupByLead(false);
            }}
          />
        </div>
      </div>

      {SECTIONS.map((s) => (
        <BoardSection
          key={s.label}
          label={s.label}
          number={s.number}
          deals={filterByPerson(s.keys.flatMap((key) => data.sections[key] ?? []))}
          groupByLead={groupByLead}
          groupByType={groupByType}
          highlightUpdates={highlightUpdates}
          tintUnassigned={s.tintUnassigned}
          listId={data.list_id}
          activityMode={activityMode}
          activityGlobalMax={activityGlobalMax}
          groupByActive={groupByActive}
        />
      ))}
    </div>
  );
}
